/**
 * Channel-level Scorecard Dashboard.
 *
 * Shows the scorecard for ONE channel (IB):
 * - Overall channel achievement %
 * - KPI breakdown rolled up across all STAFF under this channel
 * - Team list: every STAFF in this channel with their personal status
 *
 * Used inside IBDetailsPage as the "Channel Dashboard" tab.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/SectionHeader';
import { Target, RefreshCw, AlertCircle, Users, Award } from 'lucide-react';
import {
  OverallSummaryCard,
  ScorecardBreakdown,
  PeriodSelector,
  StatusBadge,
  ProgressBar,
  Sparkline,
  STATUS_META,
} from '@/components/scorecard/ScorecardPrimitives';
import CSATScorecardCard from '@/components/CSATScorecardCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NoIbNodeWarning({ ib, triedNames = [], onSaved }) {
  const [suggestions, setSuggestions] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!ib?.id) return;
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/scorecard/channel-aliases/${ib.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuggestions(r.data?.suggestions || []);
        setAliases(r.data?.aliases || []);
      } catch (e) {
        console.warn('Failed to fetch aliases:', e);
      }
    };
    fetchSuggestions();
  }, [ib?.id]);

  const saveAliases = async (list) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/scorecard/channel-aliases/${ib.id}`,
        { aliases: list },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAliases(list);
      toast.success('Alias disimpan. Refreshing...');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Gagal save: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/20 rounded-xl p-5" data-testid="no-ib-node-warning">
      <div className="flex items-start gap-3 mb-4">
        <AlertCircle className="text-[#3B82F6] flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <h3 className="text-white font-semibold mb-1">Channel ni belum di-link ke Scorecard</h3>
          <p className="text-sm text-[#9CA3AF]">
            Sistem cari IB org node dengan description = <code className="text-[#3B82F6]">{`Channel: ${ib?.name}`}</code> dan {triedNames.length > 1 ? `${triedNames.length - 1} alias lain` : '0 alias'} tapi tak jumpa.
          </p>
        </div>
      </div>

      <div className="bg-[hsl(var(--surface))] rounded-lg p-4 space-y-3">
        <div>
          <p className="text-xs font-semibold text-white mb-1">💡 Kalau channel ni dah di-rename:</p>
          <p className="text-[11px] text-[#9CA3AF]">
            Cth: dulu nama "FGC", sekarang dah jadi "{ib?.name}". External Scorecard mungkin masih guna nama lama.
            Tambah alias di bawah supaya sistem boleh match.
          </p>
        </div>

        {aliases.length > 0 && (
          <div>
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Aliases sedia ada</p>
            <div className="flex flex-wrap gap-1.5">
              {aliases.map(a => (
                <span key={a} className="inline-flex items-center gap-1 bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981] text-[11px] px-2 py-0.5 rounded-full">
                  {a}
                  <button onClick={() => saveAliases(aliases.filter(x => x !== a))}
                    className="hover:text-emerald-100" data-testid={`alias-remove-${a}`}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Pick nama dari external Scorecard:</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {suggestions.filter(s => s.name && !aliases.includes(s.name) && s.name !== ib?.name).map(s => (
              <button key={s.node_id} onClick={() => saveAliases([...aliases, s.name])}
                disabled={saving}
                data-testid={`alias-suggest-${s.node_id}`}
                className="text-[11px] bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/20 px-2 py-1 rounded-md">
                + {s.name}
              </button>
            ))}
            {suggestions.length === 0 && (
              <span className="text-[10px] text-[#6B7280]">Tiada suggestion. Sync snapshot dulu.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              placeholder="Atau taip nama lama channel (cth: FGC)"
              data-testid="alias-input"
              className="flex-1 bg-transparent border border-[hsl(var(--border))] rounded-md px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#3B82F6]/50"
            />
            <button onClick={() => {
              if (!newAlias.trim()) return;
              saveAliases([...aliases, newAlias.trim()]);
              setNewAlias('');
            }} disabled={saving || !newAlias.trim()}
              data-testid="alias-add-btn"
              className="bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 text-[#10B981] text-xs px-3 py-1.5 rounded-md disabled:opacity-50">
              + Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ChannelScorecardContent({ ib }) {
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [expandedStaffId, setExpandedStaffId] = useState(null);

  const fetchScorecard = async (p = period) => {
    if (!ib?.name) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [r, tr] = await Promise.all([
        axios.get(`${API}/scorecard/channel/${encodeURIComponent(ib.name)}?period=${p}&ib_id=${encodeURIComponent(ib.id || '')}`,
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scorecard/trend?scope=channel&scope_id=${encodeURIComponent(ib.name)}&months=6&period_end=${p}`,
          { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(r.data);
      setTrend(tr.data?.trend || []);
      // Auto-switch to latest available period if current has 0 metrics
      if (!autoSwitched && r.data?.success && (r.data.breakdown?.length || 0) === 0) {
        const available = r.data.available_periods || [];
        if (available.length > 0 && available[0] !== p) {
          setAutoSwitched(true);
          setPeriod(available[0]);
        }
      }
    } catch (e) {
      toast.error('Gagal load channel scorecard: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScorecard(period); /* eslint-disable-next-line */ }, [ib?.id, period]);

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto w-full" data-testid="channel-scorecard-content">
      <SectionHeader
        icon={Target}
        title="Channel Dashboard"
        subtitle={`Scorecard performance untuk ${ib?.name || 'channel ni'}`}
        iconBg="bg-gradient-to-br from-[#10B981] to-[#3B82F6] shadow-[#10B981]/20"
        action={
          <div className="flex items-center gap-2">
            <PeriodSelector value={period} onChange={setPeriod} available={data?.available_periods || []} />
            <Button onClick={() => fetchScorecard(period)} disabled={loading} size="sm" variant="outline"
              data-testid="refresh-channel-scorecard-btn"
              className="h-9 border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        }
      />

      {loading && !data && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
          <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
          <p className="text-sm text-[#9CA3AF]">Loading...</p>
        </div>
      )}

      {data?.error === 'no_snapshot' && (
        <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="text-white font-semibold mb-1">Scorecard belum di-sync</h3>
              <p className="text-sm text-[#9CA3AF]">Admin perlu sync data dari external Scorecard API dulu (Admin Panel → Scorecard → Sync).</p>
            </div>
          </div>
        </div>
      )}

      {data?.error === 'no_ib_node' && (
        <NoIbNodeWarning ib={ib} triedNames={data.tried_names} onSaved={() => fetchScorecard(period)} />
      )}

      {data?.success && (
        <>
          {/* Overall summary + IB info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5 h-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                    <Award className="text-[#10B981]" size={14} />
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Channel Identity</p>
                </div>
                <p className="text-white text-lg font-semibold leading-tight">{data.ib_node?.name || ib?.name}</p>
                <p className="text-xs text-[#6B7280] mt-1">{data.ib_node?.channel_name || ib?.name}</p>
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] space-y-1.5">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-[#6B7280]">Team Size</span>
                    <span className="text-white font-mono">{data.staff?.length || 0}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-[#6B7280]">Weight</span>
                    <span className="text-white font-mono">{data.ib_node?.weight || 0}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-[#6B7280]">Last sync</span>
                    <span className="text-[#9CA3AF]">{data.snapshot_fetched_at ? new Date(data.snapshot_fetched_at).toLocaleDateString('en-MY', { month: 'short', day: 'numeric' }) : '—'}</span>
                  </div>
                </div>
                {trend.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                    <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">6-month trend</p>
                    <Sparkline data={trend} width={200} height={32} />
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <OverallSummaryCard overall={data.overall} label={`Channel Overall — ${data.period}`} />
            </div>
          </div>

          {/* KPI Breakdown */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white text-base font-semibold">Channel KPI Breakdown</h2>
              <span className="text-xs text-[#6B7280]">{data.breakdown?.length || 0} metric{(data.breakdown?.length || 0) === 1 ? '' : 's'} (rolled up across team)</span>
            </div>
            <ScorecardBreakdown breakdown={data.breakdown || []} />
          </div>

          {/* CSAT (Customer Satisfaction) — standalone 100% score */}
          <CSATScorecardCard
            ibId={ib?.id}
            period={period}
            onJumpToCSAT={() => window.dispatchEvent(new CustomEvent('ib:switch-tab', { detail: { tab: 'csat' } }))}
          />

          {/* Team performance */}
          {data.staff && data.staff.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-[#9CA3AF]" />
                <h2 className="text-white text-base font-semibold">Team Performance</h2>
                <span className="text-xs text-[#6B7280]">({data.staff.length})</span>
              </div>

              {/* Team comparison horizontal bar chart */}
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 mb-2">
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-3">Achievement Comparison</p>
                <div className="space-y-2">
                  {data.staff.map((s) => {
                    const meta = STATUS_META[s.overall?.status || 'no_data'];
                    const pct = Math.min(100, Math.max(0, s.overall?.overall_pct || 0));
                    return (
                      <div key={`bar-${s.node_id}`} className="flex items-center gap-3 group cursor-pointer"
                        onClick={() => setExpandedStaffId(expandedStaffId === s.node_id ? null : s.node_id)}>
                        <span className="text-xs text-white font-medium w-28 truncate">{s.name || 'Unnamed'}</span>
                        <div className="flex-1 relative h-5 bg-[hsl(var(--surface-elevated))] rounded-md overflow-hidden">
                          <div className="h-full rounded-md transition-all duration-700"
                            style={{ width: `${pct}%`, backgroundColor: meta.color, opacity: 0.85 }} />
                          <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold tabular-nums" style={{ color: pct > 50 ? '#fff' : meta.color }}>
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                        <StatusBadge status={s.overall?.status || 'no_data'} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                {data.staff.map((s) => {
                  const expanded = expandedStaffId === s.node_id;
                  return (
                    <div key={s.node_id} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid={`staff-row-${s.node_id}`}>
                      <button
                        onClick={() => setExpandedStaffId(expanded ? null : s.node_id)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[hsl(var(--surface-elevated))]/40 transition text-left"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#10B981] to-[#3B82F6] flex items-center justify-center text-white font-bold text-xs">
                          {(s.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{s.name || 'Unnamed'}</p>
                          <p className="text-[10px] text-[#6B7280] mt-0.5">
                            <span className="text-[#10B981]">{s.overall?.on_track_count || 0}</span> on-track ·
                            <span className="text-[#F59E0B] ml-1">{s.overall?.at_risk_count || 0}</span> at-risk ·
                            <span className="text-[#E63946] ml-1">{s.overall?.off_track_count || 0}</span> off-track ·
                            <span className="ml-1 text-[#6B7280]">{s.breakdown?.length || 0} metric</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-white">{Math.min(s.overall?.overall_pct || 0, 100).toFixed(1)}<span className="text-[10px] text-[#6B7280]">%</span></p>
                          <div className="w-20 mt-1">
                            <ProgressBar pct={s.overall?.overall_pct || 0} status={s.overall?.status || 'no_data'} />
                          </div>
                        </div>
                        <StatusBadge status={s.overall?.status || 'no_data'} />
                      </button>
                      {expanded && (
                        <div className="border-t border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
                          <ScorecardBreakdown breakdown={s.breakdown || []} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
